"use client";

import React from "react";
import { Box, Typography, Divider, Stack, Link, SvgIcon } from "@mui/material";

export function CheckCircleTwoTone({ circleColor = "#F9F4EE", checkColor = "#5C87A0", size = 20 }) {
  return (
    <SvgIcon sx={{ fontSize: size }} viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="22" fill={circleColor} />
      <path
        d="M18 24L22 28L30 20"
        stroke={checkColor}
        fill="none"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  );
}

export type SectionOrder = "reminder" | "details" | "messages" | "steps";

interface Section {
  label: string;
  title?: string;
  description?: string;
  link?: { text: string; href: string };
}

interface AppointmentSummaryProps {
  reminderTitle?: string;
  reminderDescription?: any;
  messageTitle?: string;
  messageDescription?: string;
  stepsTitle?: string;
  steps?: string[];
  sections?: Section[];
  sectionsOrder?: readonly SectionOrder[];
}

const styles = {
  reminderBox: {
    textAlign: "left",
    p: {
      xs: 4,
      md: 5,
    },
    borderRadius: 2,
    backgroundColor: "#F9F4EE",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  messageBox: {
    textAlign: "left",
    p: 5,
    borderRadius: 2,
    backgroundColor: "#F6F9FC",
  },
  label: { color: "#848484", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", mb: 1 },
  title: { color: "#000", fontSize: "15px", lineHeight: "20px", fontWeight: 700 },
  title2: { color: "#000", fontSize: "15px", lineHeight: "20px", fontWeight: 700 },
  appointmentDetails: { mt: 6, p: { xs: 4, md: 6 }, borderRadius: 2, backgroundColor: "#F6F9FC", textAlign: "left" },
  link: {
    display: "flex",
    alignItems: "center",
    mt: 2,
    mb: 2,
    color: "#3F6AB2",
    fontSize: "13px",
    fontWeight: 600,
    textDecoration: "underline !important",
  },
  description: { color: "#000", fontSize: "14px", fontWeight: 400 },
  stepsContainer: { maxWidth: 462, margin: "auto", textAlign: "left" },
  steps: { display: "flex", alignItems: "center", gap: 3 },
};

const InfoSection = ({ label, title, description, link }: any) => {
  return (
    <Box>
      <Typography sx={styles.label}>{label}</Typography>
      {title && <Typography sx={styles.title}>{title}</Typography>}
      {description && (
        <Typography sx={styles.description}>
          {description.split("\n").map((line, i) => (
            <span key={i}>
              {line}
              <br />
            </span>
          ))}
        </Typography>
      )}
      {link && (
        <Link href={link.href} sx={styles.link}>
          {link.text}
        </Link>
      )}
    </Box>
  );
};

const DEFAULT_ORDER: readonly SectionOrder[] = ["reminder", "details", "messages", "steps"];

const AppointmentSummary: React.FC<AppointmentSummaryProps> = ({
  reminderTitle,
  reminderDescription,
  messageTitle,
  messageDescription,
  stepsTitle,
  steps = [],
  sections,
  sectionsOrder = DEFAULT_ORDER,
}) => {
  const renderSection = (sectionType: "reminder" | "details" | "messages" | "steps", index: number) => {
    const isFirst = index === 0;

    switch (sectionType) {
      case "reminder":
        if (!reminderTitle && !reminderDescription) return null;
        return (
          <Box sx={{ ...styles.reminderBox, mt: isFirst ? 0 : 6 }}>
            <Typography sx={styles.title2}>{reminderTitle}</Typography>
            <Typography
              sx={{ color: "#333333", fontSize: "14px", fontWeight: 400, lineHeight: "21px" }}
              dangerouslySetInnerHTML={{ __html: reminderDescription }}
            />
          </Box>
        );
      case "details":
        if (!sections) return null;
        return (
          <Box sx={{ ...styles.appointmentDetails, mt: isFirst ? 0 : 6 }}>
            {sections.map((section, idx) => (
              <React.Fragment key={idx}>
                <InfoSection {...section} />
                {idx < sections.length - 1 && <Divider sx={{ my: 5, borderColor: "rgba(0,0,0,0.10)" }} />}
              </React.Fragment>
            ))}
          </Box>
        );
      case "messages":
        if (!messageTitle && !messageDescription) return null;
        return (
          <Box sx={{ ...styles.messageBox, mt: isFirst ? 0 : 6 }}>
            <Typography sx={styles.title2}>{messageTitle}</Typography>
            <Typography
              sx={{ color: "#333333", fontSize: "14px", fontWeight: 400, lineHeight: "21px" }}
              dangerouslySetInnerHTML={{ __html: messageDescription ?? "" }}
            />
          </Box>
        );
      case "steps":
        if (!stepsTitle && !steps) return null;
        return (
          <Box sx={{ mt: isFirst ? 0 : 6 }}>
            <Typography variant="h5" fontWeight={700} sx={{ textAlign: { xs: "left", md: "center" } }} mb={5}>
              {stepsTitle}
            </Typography>
            <Stack spacing={3} sx={styles.stepsContainer}>
              {steps.map((step, idx) => (
                <Box key={idx} sx={styles.steps}>
                  <CheckCircleTwoTone size={32} />
                  <Typography variant="body2" dangerouslySetInnerHTML={{ __html: step }} />
                </Box>
              ))}
            </Stack>
          </Box>
        );
      default:
        return null;
    }
  };

  return <Box>{sectionsOrder.map((sectionType, index) => renderSection(sectionType, index))}</Box>;
};

export default AppointmentSummary;
